import React, { useState, useEffect } from 'react';
import {
  FiMessageSquare as Slack,
  FiMail as Mail,
  FiGlobe as Webhook,
  FiCheck as Check,
  FiAlertCircle as AlertCircle,
  FiSettings as Settings,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import SlackConfigModal from './SlackConfigModal';

interface IntegrationConfig {
  id: string;
  name: string;
  description: string;
  icon: IconType;
  color: string;
  configurable: boolean;
  alwaysActive?: boolean;
  comingSoon?: boolean;
}

interface IntegrationStatus {
  configured: boolean;
  config: SlackConfig | null;
}

interface SlackConfig {
  webhook_url?: string;
  channel?: string;
}

interface ColorClasses {
  bg: string;
  icon: string;
  border: string;
  badge: string;
}

// Integration channel configurations
const INTEGRATIONS: IntegrationConfig[] = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Send notifications to a Slack channel',
    icon: Slack,
    color: 'green',
    configurable: true,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Email notifications to users',
    icon: Mail,
    color: 'purple',
    configurable: false, // Email is always available via SES
    alwaysActive: true,
  },
  {
    id: 'webhook',
    name: 'Webhook',
    description: 'Send notifications to custom endpoints',
    icon: Webhook,
    color: 'blue',
    configurable: true,
    comingSoon: true,
  },
];

const getColorClasses = (color: string, isActive: boolean): ColorClasses => {
  const colors: Record<string, ColorClasses> = {
    green: {
      bg: isActive ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700',
      icon: isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500',
      border: isActive ? 'border-green-300 dark:border-green-700' : 'border-gray-200 dark:border-gray-600',
      badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    },
    purple: {
      bg: isActive ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-gray-700',
      icon: isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500',
      border: isActive ? 'border-purple-300 dark:border-purple-700' : 'border-gray-200 dark:border-gray-600',
      badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    },
    blue: {
      bg: isActive ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700',
      icon: isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500',
      border: isActive ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-600',
      badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    },
  };
  return colors[color] || colors.blue;
};

/**
 * Integrations section for the notifications settings page.
 * Displays channel cards (Slack, Email, Webhook) with configuration options.
 */
const IntegrationsSection: React.FC = () => {
  const [integrationStatus, setIntegrationStatus] = useState<Record<string, IntegrationStatus>>({
    slack: { configured: false, config: null },
    email: { configured: true, config: null }, // Email is always configured via SES
    webhook: { configured: false, config: null },
  });
  const [showSlackModal, setShowSlackModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // In a real implementation, load integration status from API
  useEffect(() => {
    // loadIntegrationStatus();
  }, []);

  const handleSlackSave = async (config: SlackConfig) => {
    setSaving(true);
    try {
      // In a real implementation, save to API
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIntegrationStatus(prev => ({
        ...prev,
        slack: { configured: true, config },
      }));
      setShowSlackModal(false);
    } catch (err) {
      console.error('Failed to save Slack config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleConfigureClick = (integrationId: string) => {
    if (integrationId === 'slack') {
      setShowSlackModal(true);
    }
    // Add handlers for other integrations as needed
  };

  return (
    <div className="space-y-4">
      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Connect notification channels to receive alerts where your team works.
      </p>

      {/* Integration Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {INTEGRATIONS.map((integration) => {
          const status = integrationStatus[integration.id];
          const isActive = !!(status?.configured || integration.alwaysActive);
          const colors = getColorClasses(integration.color, isActive);
          const Icon = integration.icon;

          return (
            <div
              key={integration.id}
              className={`relative p-5 rounded-lg border-2 transition-all ${colors.border} ${
                integration.comingSoon ? 'opacity-60' : ''
              }`}
            >
              {/* Coming Soon Badge */}
              {integration.comingSoon && (
                <span className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                  Coming Soon
                </span>
              )}

              {/* Icon */}
              <div className={`inline-flex p-3 rounded-lg ${colors.bg} mb-4`}>
                <Icon className={`w-6 h-6 ${colors.icon}`} />
              </div>

              {/* Content */}
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                {integration.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {integration.description}
              </p>

              {/* Status & Action */}
              <div className="flex items-center justify-between">
                {isActive ? (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${colors.badge}`}>
                    <Check className="w-3.5 h-3.5" />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Not configured
                  </span>
                )}

                {integration.configurable && !integration.comingSoon && (
                  <button
                    onClick={() => handleConfigureClick(integration.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    {isActive ? 'Edit' : 'Configure'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Slack Modal */}
      <SlackConfigModal
        isOpen={showSlackModal}
        onClose={() => setShowSlackModal(false)}
        onSave={handleSlackSave}
        existingConfig={integrationStatus.slack?.config}
        saving={saving}
      />
    </div>
  );
};

export default IntegrationsSection;
